import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseService } from './base/base.service';
import { SessionUserHashManagerService } from './session-user-hash-manager/session-user-hash-manager.service';
import { BrandInfo } from '../_models/reports/brand-info.model';

/**
 * The brands the signed-in staff user may see (`GET api/User/MyBrands`), fetched once per session and
 * shared by every brand filter and Brand column.
 *
 * A failed load resolves to an empty list, which hides the filter and the column — the screens behave
 * exactly as they did before brand filtering existed. The failure is not cached, so the next caller
 * retries.
 */
@Injectable({ providedIn: 'root' })
export class BrandFilterService extends BaseService {

  private apiUrl: string = environment.apiUrl;
  private myBrands$: Observable<BrandInfo[]> = null;

  constructor(
    private http: HttpClient,
    protected hashManagerService: SessionUserHashManagerService
  ) {
    super(hashManagerService);
    this.apiUrl += this.apiUrl.endsWith('/') ? '' : '/';
  }

  public getMyBrands(): Observable<BrandInfo[]> {
    if (this.myBrands$ == null) {
      this.myBrands$ = this.http.get(this.apiUrl + 'api/User/MyBrands', this.prepareOptions(null)).pipe(
        map((data: any) => (data ? data.body : null) ?? []),
        catchError(this.handleError('getMyBrands')),
        catchError(() => {
          this.myBrands$ = null;
          return of([] as BrandInfo[]);
        }),
        shareReplay(1)
      );
    }
    return this.myBrands$;
  }
}

/** Short code for a row's brand, or '' when the id is not one of the caller's brands. */
export function brandShortCode(companyId: number, brands: BrandInfo[]): string {
  if (companyId == null || !brands) {
    return '';
  }
  const brand = brands.find(b => b.companyId === Number(companyId));
  return brand ? brand.brandShortCode : '';
}
